import { useState, useContext, useEffect, useRef } from 'react';
import { BulkContext } from '../store/bulklist-filters-context';
import { Button, Snackbar, Alert } from "@mui/material";
import DownloadForOfflineOutlinedIcon from "@mui/icons-material/DownloadForOfflineOutlined";
import { FiltersContext } from '../store/filters-options-context';
import { downloadLargeCSV } from "../utils/download";
import LoadProgress from "../global/Loading";
import { getLinkDetails } from '../utils/global';
import { resolveDomainCode } from '../utils/bigquery';
import m49 from '../data/m49.json';


export default function DownloadBulkCSV() {
    const { comparingCountries, selectedItems, apiUrls, setApiUrls } = useContext(BulkContext);
    const { params, signalDataLoadingComplete } = useContext(FiltersContext);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fetchComplete, setFetchComplete] = useState(false);
    const [data, setData] = useState(null);
    const [downloadStatus, setDownloadStatus] = useState('');
    const [showPatienceNote, setShowPatienceNote] = useState(false);
    const [failedIndicators, setFailedIndicators] = useState([]);
    const [wasCancelled, setWasCancelled] = useState(false);
    // Held in a ref rather than state: the running download reads it inside a loop that
    // never re-renders, and aborting has to reach the requests already in flight.
    const abortRef = useRef(null);
    const [snackState, setSnackState] = useState({
        open: false,
        vertical: "top",
        horizontal: "center",
    });
    const { vertical, horizontal, open } = snackState;

    let years = params.year;
    const firstYear = params.year[0];
    const lastYear = params.year.slice(-1).pop();
    let area = comparingCountries.map((item) => (
        item.id
    ));

    const handleClose = () => {
        setSnackState({ ...snackState, open: false });
    };

    const cancelDownload = () => {
        abortRef.current?.abort();
    };


    const loadData = async () => {
        const startTime = performance.now();
        console.log(`[BulkDownload] Starting download of ${apiUrls.length} indicators...`);

        setIsLoading(true);
        setError(null);
        setFetchComplete(false);
        setFailedIndicators([]);
        setShowPatienceNote(false);
        setWasCancelled(false);

        const controller = new AbortController();
        abortRef.current = controller;
        let completedRequests = 0;
        const failedItems = [];
        const m49ToIso = Object.fromEntries(m49.map(item => [item['M49 Code'], item['ISO-alpha3 Code']]));
        setDownloadStatus(`Downloading: ${completedRequests} / ${apiUrls.length} indicators`);
        const patienceTimer = setTimeout(() => setShowPatienceNote(true), 30000);

        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        // 502/503/504 (and outright network/parse failures) tend to be transient overload
        // on FAO's side rather than a broken query - confirmed by retrying failed indicators
        // in isolation and seeing them succeed. Anything else (e.g. a genuine 400 from a bad
        // query) would just fail again identically, so only these get retried.
        const RETRYABLE_STATUSES = new Set([502, 503, 504]);
        const MAX_ATTEMPTS = 3;

        const fetchIndicator = async ({ url, outcome, theme, indicator_name, source }) => {
            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                try {
                    const response = await fetch(url, { signal: controller.signal });

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    const jsonData = await response.json();

                    // Area ordering is no longer done server-side (it doubled the country
                    // list length inside the ORDER BY, blowing up the request URL). The
                    // query still sorts by food_group/year/item, so we only need to sort
                    // by area within each of those groups to reproduce the original order.
                    const areaRank = new Map(area.map((code, idx) => [Number(code), idx]));
                    const groupOrder = [];
                    const groups = new Map();

                    jsonData.forEach((item) => {
                        const groupKey = `${item.food_group_code}|${item.year}|${item.item_code}`;
                        if (!groups.has(groupKey)) {
                            groups.set(groupKey, []);
                            groupOrder.push(groupKey);
                        }
                        groups.get(groupKey).push(item);
                    });

                    const sortedJsonData = groupOrder.flatMap((groupKey) =>
                        groups.get(groupKey).sort((a, b) =>
                            (areaRank.get(Number(a.area_code_m49)) ?? Infinity) -
                            (areaRank.get(Number(b.area_code_m49)) ?? Infinity)
                        )
                    );

                    const modifiedData = sortedJsonData.map((item) => ({
                        outcome: outcome,
                        theme: theme,
                        indicator_name: indicator_name,
                        source: source,
                        iso3: m49ToIso[item.area_code_m49],
                        ...item,
                    }));

                    completedRequests++;
                    setDownloadStatus(
                        `Downloading: ${completedRequests} / ${apiUrls.length} indicators`
                    );

                    return modifiedData;
                } catch (error) {
                    // Cancelling is not a failure: it must not be retried, and the indicator
                    // must not end up in the "could not be downloaded" list.
                    if (error?.name === "AbortError" || controller.signal.aborted) return null;

                    const statusMatch = /^HTTP (\d+)$/.exec(error.message || "");
                    const status = statusMatch ? Number(statusMatch[1]) : null;
                    const isRetryable = status === null || RETRYABLE_STATUSES.has(status);

                    if (isRetryable && attempt < MAX_ATTEMPTS) {
                        await sleep(1000 * attempt);
                        continue;
                    }

                    console.error(`Error fetching ${indicator_name} (${url}) after ${attempt} attempt(s):`, error);
                    failedItems.push({ indicator_name, reason: error.message || "Unknown error" });
                    completedRequests++;
                    return null;
                }
            }
        };

        try {
            // Large selections (e.g. every indicator at World scope) used to fire every
            // request at once, which overwhelmed FAO's backend and produced widespread
            // 502/504s. Requests are now sent in small concurrent batches instead.
            const BATCH_SIZE = 3;
            const responses = [];
            for (let i = 0; i < apiUrls.length; i += BATCH_SIZE) {
                if (controller.signal.aborted) break;
                const batch = apiUrls.slice(i, i + BATCH_SIZE);
                const batchResults = await Promise.all(batch.map(fetchIndicator));
                responses.push(...batchResults);
            }

            if (controller.signal.aborted) {
                setDownloadStatus("");
                setWasCancelled(true);
                return;
            }

            const data = responses.filter((response) => response !== null);
            const flattenedData = data.flat();

            const noData = data.every(subArray => subArray.length === 0);

            setData(flattenedData);
            setDownloadStatus("");
            setFailedIndicators(failedItems);



            !noData
                ? downloadLargeCSV(
                    new Date(),
                    flattenedData,
                    `AFS_Bulk`,
                    comparingCountries.length > 1
                        ? `${comparingCountries.length}countries`
                        : `${comparingCountries[0].label}`,
                    selectedItems.length > 1
                        ? `${selectedItems.length}indicators`
                        : `1indicator`,
                    `${firstYear}_${lastYear}`
                )
                : setSnackState({ ...snackState, open: true });
        } catch (error) {
            if (error?.name === "AbortError" || controller.signal.aborted) {
                setDownloadStatus("");
                setWasCancelled(true);
            } else {
                setError(error.message || "Unknown error occurred");
            }
        } finally {
            abortRef.current = null;
            const elapsedSeconds = ((performance.now() - startTime) / 1000).toFixed(1);
            console.log(`[BulkDownload] Finished in ${elapsedSeconds}s (${failedItems.length} failed)`);

            clearTimeout(patienceTimer);
            setShowPatienceNote(false);
            setIsLoading(false);
            setFetchComplete(true);
            // Signal that data loading is complete to disable slider temporarily
            signalDataLoadingComplete();
        }
    };


    useEffect(() => {
        if (!selectedItems || selectedItems.length === 0) {
            return;
        }

        const generatedUrls = selectedItems
            .map((item) => {

                const outcome = item.outcome;
                const theme = item.theme;
                const label = item.value.label;
                const source = item.value.domain_code ? item.value.domain_code.split('_').pop().toUpperCase() : "";
                const { text } = getLinkDetails(source);
                const source_text = text;


                const domain_code = resolveDomainCode(item.value.domain_code);
                const sex_code = item.value.sex_code ? item.value.sex_code : false;
                const urban_rural = item.value.urban_rural ? item.value.urban_rural : false;
                const sort_by_item = item.value.sort_by_item ? item.value.sort_by_item : false;
                const sort_by_element = item.value.sort_by_element ? item.value.sort_by_element : false;
                const indicator_code = item.value.indicator_code ? item.value.indicator_code : false;
                const foodGroup = item.value.food_group ? item.value.food_group : false;

                // Some domains store item_code/element_code as BigQuery INT64 columns; mockData.js
                // marks those cases by using JS numbers (e.g. item_code: [1, 2]) instead of strings
                // (e.g. ["1"]). Quoting a number as a SQL string literal against an INT64 column
                // fails with "No matching signature for operator IN for argument types INT64 and
                // STRING", so only string codes get wrapped in quotes here.
                const formatCode = (code) => (typeof code === "number" ? `${code}` : `"${String(code).trim()}"`);

                const elementCodes = Array.isArray(item.value.element_code) && item.value.element_code.length > 0
                    ? item.value.element_code.map(formatCode)
                    : [];

                const itemCodes = Array.isArray(item.value.item_code)
                    ? item.value.item_code.map(formatCode)
                    : [];

                const foodGroupOrderCase = foodGroup
                    ? `CASE food_group_code ${foodGroup
                        .map((code, index) => `WHEN "${code}" THEN ${index + 1}`)
                        .join(" ")} ELSE 9999 END`
                    : "";

                const elementCodesCase = elementCodes
                    ? `CASE element_code ${elementCodes
                        .map((code, index) => `WHEN "${code}" THEN ${index + 1}`)
                        .join(" ")} ELSE 9999 END,`
                    : "";

                const yearsOrderCase = years
                    ? `CASE year ${years
                        .map((code, index) => `WHEN "${code}" THEN ${index + 1}`)
                        .join(" ")} ELSE 9999 END`
                    : "";

                const itemCodeOrderCase = sort_by_item
                    ? `CASE item_code ${itemCodes
                        .map((code, index) => `WHEN ${code} THEN ${index + 1}`)
                        .join(" ")} ELSE 9999 END`
                    : "";

                // Area is intentionally not part of the SQL ORDER BY: a WHEN per selected
                // country here duplicated the (potentially very long, e.g. "World") country
                // list already present in the WHERE clause, which is what pushed the request
                // URL past server limits (414). Country order is now applied client-side
                // after the fetch, within each food_group/year/item group.
                const orderByClause = [foodGroupOrderCase, yearsOrderCase, itemCodeOrderCase]
                    .filter(Boolean)
                    .join(", ");

                // A handful of domains store area_code_m49 as a STRING column rather than
                // INT64 (confirmed per-domain via BigQuery's INFORMATION_SCHEMA); mockData.js
                // flags those with area_code_is_string so the literal gets quoted accordingly.
                const areaCodes = item.value.area_code_is_string
                    ? area.map((code) => `"${code}"`)
                    : area.map((code) => `${code}`);

                const sqlQuery = `SELECT * FROM ${domain_code} WHERE area_code_m49 IN (${areaCodes.join(", ")})
                        AND year IN (${years.map((year) => `"${year}"`).join(", ")}) 
                        ${elementCodes && elementCodes.length > 0 ? `AND element_code IN (${elementCodes.join(", ")}) ` : ``}
                        ${indicator_code
                            ? `AND indicator_code IN (${indicator_code.map((indicator_code) => `"${indicator_code}"`).join(", ")}) `
                            : (itemCodes.length > 0 ? `AND item_code IN (${itemCodes.join(", ")}) ` : ``)}
                        ${foodGroup ? `AND food_group_code IN (${foodGroup.map((food_group) => `"${food_group}"`).join(", ")}) ` : ``}
                        ${sex_code ? `AND sex_code IN (${sex_code.map((sex_code) => `"${sex_code}"`).join(", ")}) ` : ""}
                        ${urban_rural ? `AND urban_rural IN (${urban_rural.map((urban_rural) => `"${urban_rural}"`).join(", ")}) ` : ""}
                    ${orderByClause ? `ORDER BY ${orderByClause} ` : ""}
                LIMIT 30000`;

                const url = `https://data.fao.org/api/v2/bigquery?query=${sqlQuery}&output_format=json&download=false`;
                //const url = `https://data.review.fao.org/bigquery?query=${sqlQuery}&output_format=json&download=false`;

                return {
                    url,
                    outcome: outcome,
                    indicator_name: label,
                    theme: theme,
                    source: text
                };

            });

        setApiUrls(generatedUrls);


    }, [selectedItems, comparingCountries]);



    return (
        <>
            <LoadProgress
                isFetching={isLoading}
                text={
                    showPatienceNote ? (
                        <>
                            {downloadStatus}
                            <br />
                            <span style={{ fontSize: "11px", opacity: 0.85 }}>
                                Download is proceeding, please wait...
                            </span>
                        </>
                    ) : (
                        downloadStatus
                    )
                }
                // Shown for the whole download, not only once it is running late: gating it
                // on the 30-second patience note meant that on a slow-but-not-that-slow
                // download there was no way out at all.
                action={
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={cancelDownload}
                        sx={{
                            color: "white",
                            borderColor: "rgba(255,255,255,0.6)",
                            "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.08)" },
                        }}
                    >
                        Cancel download
                    </Button>
                }
            />

            <Snackbar
                anchorOrigin={{ vertical, horizontal }}
                open={open}
                onClose={handleClose}
                autoHideDuration={5000}
                key={vertical + horizontal}
                sx={{ height: "100%" }}
            >
                <Alert
                    severity="warning"
                    sx={{ border: '1px solid orange', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}

                >
                    No results for this selection. <br /> Please try different countries, indicators or year range.
                </Alert>
            </Snackbar>

            <Snackbar
                anchorOrigin={{ vertical, horizontal }}
                open={wasCancelled}
                onClose={() => setWasCancelled(false)}
                autoHideDuration={5000}
                key={`cancelled-${vertical}-${horizontal}`}
            >
                <Alert severity="info" onClose={() => setWasCancelled(false)}>
                    Download cancelled. No file was saved.
                </Alert>
            </Snackbar>

            <Snackbar
                anchorOrigin={{ vertical, horizontal }}
                open={failedIndicators.length > 0}
                onClose={() => setFailedIndicators([])}
                key={`failed-${vertical}-${horizontal}`}
            >
                <Alert
                    severity="error"
                    onClose={() => setFailedIndicators([])}
                    sx={{ border: '1px solid red', maxWidth: 420, maxHeight: 320, overflowY: "auto" }}
                >
                    {failedIndicators.length} indicator{failedIndicators.length > 1 ? "s" : ""} could not be downloaded:
                    <ul style={{ margin: "4px 0 0", paddingLeft: "1.2em" }}>
                        {failedIndicators.map((failed, index) => (
                            <li key={index}>{failed.indicator_name}: {failed.reason}</li>
                        ))}
                    </ul>
                </Alert>
            </Snackbar>

            <Snackbar
                anchorOrigin={{ vertical, horizontal }}
                open={!!error}
                onClose={() => setError(null)}
                autoHideDuration={8000}
                key={`error-${vertical}-${horizontal}`}
                sx={{ height: "100%" }}
            >
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ border: '1px solid red' }}
                >
                    {error}
                </Alert>
            </Snackbar>

            <Button
                variant="contained"
                size="medium"
                disabled={!selectedItems.length > 0 || !comparingCountries.length > 0}
                startIcon={<DownloadForOfflineOutlinedIcon />}
                onClick={loadData}
                sx={{ flex: 1 }}
            >
                Download
            </Button>
        </>
    )
}